import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../lib/supabase';
import { useAuth } from './useAuth';
import { getEnvVar } from '../lib/env';
import type { UserCoursePurchase } from '../types';

interface CoursePrice {
  price: number;
  currency: string;
  formatted: string;
}

export function useStripe() {
  const supabase = getSupabaseClient();
  const { user } = useAuth();

  // Check if user has purchased a specific course
  const useCoursePurchase = (courseId: string | undefined) => {
    return useQuery({
      queryKey: ['course-purchase', courseId],
      queryFn: async () => {
        if (!courseId || !user) return null;

        const { data, error } = await supabase
          .from('user_course_purchases')
          .select('*')
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        return data as UserCoursePurchase | null;
      },
      enabled: !!courseId && !!user,
    });
  };

  // Get all user purchases from database (no sync - webhook handles it)
  const { data: purchases, isLoading: isLoadingPurchases } = useQuery({
    queryKey: ['user-purchases'],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_course_purchases')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data as UserCoursePurchase[];
    },
    enabled: !!user,
    staleTime: 60 * 1000, // Cache for 1 minute (webhook keeps it fresh)
  });

  // Get Stripe customer portal URL
  const getCustomerPortalUrl = async (courseId?: string): Promise<string> => {
    const token = await supabase.auth.getSession();
    const apiUrl = getEnvVar('VITE_API_URL');
    
    const params = new URLSearchParams({
      return_url: window.location.origin + '/courses',
    });

    if (courseId) {
      params.append('course_id', courseId);
    }

    const response = await fetch(`${apiUrl}/api/stripe/customer-portal?${params}`, {
      headers: {
        'Authorization': `Bearer ${token.data.session?.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get customer portal URL');
    }

    const data = await response.json();
    return data.url;
  };

  // Check if user has access to a course
  const hasAccessToCourse = (courseId: string, priceId: string | null): boolean => {
    // Free courses (price_id is null or '0')
    if (!priceId || priceId === '0') {
      return true;
    }

    // Check if user has purchased the course
    return purchases?.some((p: UserCoursePurchase) => p.course_id === courseId) ?? false;
  };

  // Get course price from Stripe
  const useCoursePrice = (courseId: string | undefined) => {
    const apiUrl = getEnvVar('VITE_API_URL');
    
    return useQuery({
      queryKey: ['course-price', courseId],
      queryFn: async (): Promise<CoursePrice> => {
        const response = await fetch(`${apiUrl}/api/course/price?course_id=${courseId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch price');
        }
        
        return response.json();
      },
      enabled: !!courseId,
      staleTime: 60 * 60 * 1000, // Cache for 1 hour
    });
  };

  return {
    purchases,
    isLoadingPurchases,
    useCoursePurchase,
    getCustomerPortalUrl,
    hasAccessToCourse,
    useCoursePrice,
  };
}
